import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { useSettings } from "../lib/settings";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ProductCard, { ProductSkeleton } from "../components/ProductCard";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "../components/ui/select";
import { Search, X } from "lucide-react";

const TYPE_LABELS = {
  supplement: { title: "المكملات الغذائية", sub: "جميع المنتجات الأصلية والموثوقة" },
  clothing:   { title: "الملابس الرياضية", sub: "أزياء تدريب بأعلى مستوى راحة" },
  accessory:  { title: "الإكسسوارات الرياضية", sub: "كل ما تحتاجه لتجربة تدريب مثالية" },
};

export default function Category({ type }) {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [agencies, setAgencies] = useState([]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState(params.get("sort") || "newest");
  const [agencyId, setAgencyId] = useState(params.get("agency") || "");
  const [loading, setLoading] = useState(true);
  const { settings } = useSettings();

  const label = TYPE_LABELS[type];
  const bannerKey = { supplement: "category_banner_supplement", clothing: "category_banner_clothing", accessory: "category_banner_accessory" }[type];
  const bannerUrl = settings?.[bannerKey] || "";

  useEffect(() => {
    if (type === "supplement") {
      api.get("/agencies").then((r) => setAgencies(r.data)).catch(()=>{});
    }
  }, [type]);

  const load = useCallback(async (opts = {}) => {
    setLoading(true);
    try {
      const p = opts.page ?? page;
      const r = await api.get("/products", {
        params: {
          type,
          q: q || undefined,
          agency_id: agencyId || undefined,
          sort,
          page: p,
          limit: 12,
        },
      });
      if (p === 1) setItems(r.data.items);
      else setItems((cur) => [...cur, ...r.data.items]);
      setTotal(r.data.total);
    } finally { setLoading(false); }
  }, [type, q, agencyId, sort, page]);

  useEffect(() => { setPage(1); load({ page: 1 }); /* eslint-disable-next-line */ }, [type, sort, agencyId]);

  useEffect(() => {
    // sync URL
    const next = {};
    if (q) next.q = q;
    if (agencyId) next.agency = agencyId;
    if (sort && sort !== "newest") next.sort = sort;
    setParams(next, { replace: true });
    // eslint-disable-next-line
  }, [q, agencyId, sort]);

  const onSearch = (e) => {
    e.preventDefault();
    setPage(1);
    load({ page: 1 });
  };

  const clearFilters = () => { setQ(""); setAgencyId(""); setSort("newest"); setPage(1); };

  const loadMore = () => { const np = page + 1; setPage(np); load({ page: np }); };

  return (
    <div className="App">
      <Header />
      <div className="pg-page-top">
        {/* Header banner */}
        {bannerUrl ? (
          <div data-testid="category-banner" className="relative w-full h-56 md:h-72 overflow-hidden mb-8">
            <img src={fileUrl(bannerUrl)} alt={label.title} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-l from-[#191A1D]/95 via-[#191A1D]/70 to-[#191A1D]/40" />
            <div className="absolute inset-0 pg-grid-lines opacity-30" />
            <div className="pg-container relative h-full flex flex-col justify-end pb-6">
              <div className="text-[#EF1B17] text-xs font-bold uppercase tracking-[0.2em] mb-2">— قسم كامل</div>
              <h1 data-testid="category-title" className="text-3xl md:text-5xl font-black">{label.title}</h1>
              <p className="text-gray-300 mt-1">{label.sub}</p>
            </div>
          </div>
        ) : (
          <div className="pg-container">
            <div className="text-[#EF1B17] text-xs font-bold uppercase tracking-[0.2em] mb-2">— قسم كامل</div>
            <h1 data-testid="category-title" className="text-3xl md:text-5xl font-black mb-2">{label.title}</h1>
            <p className="text-gray-400">{label.sub}</p>
          </div>
        )}

        {/* Filters */}
        <div className="pg-container mt-4">
          <div className="bg-[#24262B] border border-[#3A3D43] rounded-lg p-4 md:p-5 flex flex-col md:flex-row gap-3 md:items-center">
            <form onSubmit={onSearch} className="relative flex-1 min-w-0">
              <input
                data-testid="cat-search-input"
                value={q} onChange={(e) => setQ(e.target.value)}
                placeholder={type === "supplement" ? "ابحث بالمنتج أو الوكالة..." : "ابحث بالمنتج..."}
                className="w-full bg-[#191A1D] border border-[#3A3D43] rounded-md pl-4 pr-10 py-2.5 text-sm focus:border-[#EF1B17] focus:outline-none"
              />
              <button type="submit" className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#EF1B17]">
                <Search size={16} />
              </button>
            </form>
            {type === "supplement" && (
              <Select value={agencyId || "all"} onValueChange={(v) => setAgencyId(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="cat-agency-filter" className="md:w-56 bg-[#191A1D] border-[#3A3D43]">
                  <SelectValue placeholder="كل الوكالات" />
                </SelectTrigger>
                <SelectContent className="bg-[#24262B] border-[#3A3D43] text-[#F4F4F2]">
                  <SelectItem value="all">كل الوكالات</SelectItem>
                  {agencies.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger data-testid="cat-sort" className="md:w-48 bg-[#191A1D] border-[#3A3D43]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#24262B] border-[#3A3D43] text-[#F4F4F2]">
                <SelectItem value="newest">الأحدث</SelectItem>
                <SelectItem value="price_asc">السعر: من الأقل</SelectItem>
                <SelectItem value="price_desc">السعر: من الأعلى</SelectItem>
                <SelectItem value="featured">الأكثر تميّزاً</SelectItem>
              </SelectContent>
            </Select>
            {(q || agencyId || sort !== "newest") && (
              <button data-testid="clear-filters" onClick={clearFilters} className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-[#EF1B17]">
                <X size={14} /> مسح
              </button>
            )}
          </div>
        </div>

        {/* Grid */}
        <div className="pg-container mt-8 pb-16">
          {loading && items.length === 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {Array.from({ length: 8 }).map((_, i) => <ProductSkeleton key={i} />)}
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-24">
              <div className="text-6xl font-black text-[#3A3D43] mb-4">0</div>
              <p className="text-lg text-gray-400">لا توجد نتائج مطابقة لبحثك</p>
              <button onClick={clearFilters} className="mt-4 text-[#EF1B17] font-bold">مسح الفلاتر</button>
            </div>
          ) : (
            <>
              <div className="text-sm text-gray-400 mb-4">عدد المنتجات: <span className="text-[#F4F4F2] font-bold">{total}</span></div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {items.map((p) => <ProductCard key={p.id} product={p} />)}
              </div>
              {items.length < total && (
                <div className="text-center mt-10">
                  <button data-testid="load-more" onClick={loadMore} disabled={loading}
                    className="bg-transparent border border-[#3A3D43] hover:border-[#EF1B17] text-[#F4F4F2] font-bold px-8 py-3 rounded-md pg-lift">
                    {loading ? "جاري التحميل..." : "تحميل المزيد"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
